import {
  Box,
  Flex,
  Text,
  IconButton,
  useColorModeValue,
  useDisclosure,
  HStack,
  Button,
  Tooltip,
  useBoolean,
} from '@chakra-ui/react';
import { HamburgerIcon, CloseIcon } from '@chakra-ui/icons';
import {
  BackIcon,
  CodeQuickIcon,
  CommentIcon,
  SheetEyeIcon,
} from '../../../constants/IconData';
import AppColors from '../../../constants/AppColors';
import { FONT_FAMILY } from '../../../constants/Theme';
import { useNavigate } from 'react-router-dom';
import { useContext, useState } from 'react';
import { AuditSheetContext } from '../../../providers/AuditSheetProvider';
import { format } from 'date-fns';
import AuditSheetFileViewer from './AuditSheetFileViewer';
import NotesDrawer from './NotesDrawer';
import BatchFileViewer from '../../../components/pdf_viewer/BatchFileViewer';
import { getDocumentViewerPath } from '../../../components/pdf_viewer/documentViewer.utils';

export default function Topbar({
  onAuditSubmit,
  updateAuditSheet,
  handleGoingBack,
  view,
  disableSubmit,
  lastSaved,
  onChange,
  onOpenNotes,
  onRebuttalClick,
  notesDrawerRef,
  onSendToAuditor,
  batchName,
  batchId
}) {
  // context
  const auditSheetContext = useContext(AuditSheetContext);
  const { currentUpload, allComments } = auditSheetContext;

  // "Send to Auditor" only available when at least one rebuttal thread is Accepted
  const hasAcceptedRebuttal = allComments.some(c => {
    if (c.parent || c.action !== 'REBUTTAL') return false;
    const replies = c.reply_comments || [];
    let threadStatus = 'Open';
    for (const r of replies) {
      if (r.action === 'REBUTTAL-REJECTED') threadStatus = 'Rejected';
      else if (r.action === 'REBUTTAL-RESOLVED') threadStatus = 'Closed';
      else if (r.action === 'REBUTTAL-ACCEPTED') threadStatus = 'Accepted';
      else if (r.action === 'RE-OPENED') threadStatus = 'Accepted';
    }
    return threadStatus === 'Accepted';
  });
  // other hooks
  const { isOpen, onToggle } = useDisclosure();
  const navigate = useNavigate();

  async function onBack() {
    if (!view) {
      onChange(true);
      await handleGoingBack();
      onChange(false);
    }
    navigate(-1);
  }

  const [batchFileData, setBatchFileData] = useState(null);
  const [isFetchingBatch, setIsFetchingBatch] = useState(false);
  const [isModalOpen, imoState] = useBoolean(false);

  const handleOpenBatchViewer = async () => {
    if (batchFileData) { imoState.on(); return; }  // already fetched, reuse same reference
    try {
      imoState.on();
      const res = await auditSheetContext.getFileObjectByBatchId(batchId);
      setBatchFileData(res);
    } catch (error) {
      imoState.off();
    } finally {
    }
  };

  return (
    <Box w={'100%'}>
      <Flex
        bg={AppColors.secondary}
        color={useColorModeValue('gray.600', 'white')}
        minH={'60px'}
        borderBottom={1}
        borderStyle={'solid'}
        borderColor={useColorModeValue('gray.200', 'gray.900')}
        align={'center'}
        position="relative"
      >
        <Flex
          flex={{ base: 1, md: 'auto' }}
          ml={{ base: -2 }}
          display={{ base: 'flex', md: 'none' }}
        >
          <IconButton
            onClick={onToggle}
            icon={
              isOpen ? <CloseIcon w={3} h={3} /> : <HamburgerIcon w={5} h={5} />
            }
            variant={'ghost'}
            aria-label={'Toggle Navigation'}
          />
        </Flex>
        <Flex flex={{ base: 1 }} alignItems={'center'}>
          <CodeQuickIcon
            onClick={onBack}
            style={{
              height: 62,
              marginLeft: 10,
              padding: 5,
              cursor: 'pointer',
            }}
          />
          <BackIcon
            style={{
              marginLeft: 20,
              width: 40,
              height: 40,
              cursor: 'pointer',
              display: 'inline-block',
            }}
            onClick={onBack}
          />
          <Text
            display={{ base: 'none', md: 'block' }}
            color={'white'}
            fontSize={'xl'}
            fontWeight={'bold'}
            mx={7}
          >
            {batchId ? (batchName || batchId) : currentUpload.upload_id}
          </Text>
          {currentUpload.status === 'CLIENT REBUTTAL' && (
            <Box px={3} py={1} borderRadius='md' bg='rgba(26,115,232,0.85)' border='1px solid rgba(255,255,255,0.3)'>
              <Text fontSize='xs' fontWeight='semibold' color='white' whiteSpace='nowrap'>
                Client Rebuttal
              </Text>
            </Box>
          )}
          {currentUpload.status === 'REBUTTAL IN PROGRESS' && (
            <Box px={3} py={1} borderRadius='md' bg='rgba(251,188,4,0.9)' border='1px solid rgba(255,255,255,0.3)'>
              <Text fontSize='xs' fontWeight='semibold' color='white' whiteSpace='nowrap'>
                Rebuttal In Progress
              </Text>
            </Box>
          )}
        </Flex>

        <HStack alignItems={'center'} spacing={2} gap={2} mr={1}>
          <Text
            fontSize={'sm'}
            fontFamily={FONT_FAMILY.ptSans}
            color={'white'}
            fontWeight="bold"
          >
            {lastSaved
              ? ` Last saved ${format(
                  new Date(lastSaved),
                  'MMM dd, yyyy'
                )} at ${format(new Date(lastSaved), 'p')}`
              : null}
          </Text>
          {currentUpload.status === 'CLIENT REBUTTAL' && (
            <Tooltip
              hasArrow
              label={!hasAcceptedRebuttal ? 'Accept at least one rebuttal before sending to auditor' : ''}
              bg='gray.700'
              color='white'
              isDisabled={hasAcceptedRebuttal}
            >
              <Box display="inline-block">
                <Button
                  color={'white'}
                  borderRadius="3xl"
                  bgColor={hasAcceptedRebuttal ? 'rgba(26,115,232,0.85)' : 'rgba(26,115,232,0.4)'}
                  fontSize={'sm'}
                  onClick={onSendToAuditor}
                  disabled={!hasAcceptedRebuttal}
                  _hover={{ bgColor: hasAcceptedRebuttal ? 'rgba(26,115,232,1)' : 'rgba(26,115,232,0.4)' }}
                  cursor={!hasAcceptedRebuttal ? 'not-allowed' : 'pointer'}
                >
                  Send to Auditor
                </Button>
              </Box>
            </Tooltip>
          )}
          <Tooltip hasArrow label="View rebuttal" bg="white" color="black">
            <Box display="inline-block">
              <CommentIcon
                style={{ width: 40, height: 40, cursor: 'pointer' }}
                onClick={() => onRebuttalClick()}
              />
            </Box>
          </Tooltip>
          {Object.keys(currentUpload).length ? (
            <AuditSheetFileViewer auditData={currentUpload} chart_id={currentUpload?.upload_id}/>
          ) : (
            <SheetEyeIcon
              style={{ cursor: 'pointer', height: 40, width: 40 }}
              onClick={handleOpenBatchViewer}
            />
          )}
          <NotesDrawer onOpenNotes={onOpenNotes} drawerRef={notesDrawerRef} />
          <Button
            mr={1}
            color={'white'}
            style={{
              paddingInline: '20px',
              outlineColor: AppColors.secondary,
              marginRight: '20px',
            }}
            borderRadius="3xl"
            bgColor={AppColors.yellowColor}
            fontSize={'sm'}
            onClick={onAuditSubmit}
            disabled={ view || disableSubmit || currentUpload.status === 'REBUTTAL IN PROGRESS'}
          >
            Submit Audit
          </Button>
        </HStack>
      </Flex>
      <BatchFileViewer
        isOpen={isModalOpen}
        onClose={imoState.off}
        batchFiles={batchFileData ?? []}
        viewerPath={getDocumentViewerPath({ batchId })}
      />
    </Box>
  );
}
